<template>
  <v-form
    ref="formRef"
    class="fan-form method-form"
    @submit.prevent=""
  >
    <!-- Basisdaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-counter</v-icon>
        <span>Basisdaten</span>
      </div>

      <v-text-field
        v-model.number="form.amount"
        type="number"
        :error-messages="validationErrors?.amount"
        label="Anzahl"
        :hint="hint.amount"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.amount" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.amount }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.amount"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Anzahl übernehmen"
            @click="copyFromReference('amount')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Luft- und Druckdaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-weather-windy</v-icon>
        <span>Luft- und Druckdaten</span>
      </div>

      <v-text-field
        v-model.number="form.volumeFlow"
        type="number"
        :error-messages="validationErrors?.volume_flow"
        label="Luftmenge"
        suffix="m³/h"
        :hint="hint.volume_flow"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.volume_flow" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.volume_flow }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.volume_flow"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Luftmenge übernehmen"
            @click="copyFromReference('volume_flow')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-text-field
        v-model.number="form.totalPressure"
        type="number"
        :error-messages="validationErrors?.total_pressure"
        label="Gesamtdruck"
        suffix="Pa"
        :hint="hint.total_pressure"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.total_pressure" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.total_pressure }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.total_pressure"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Gesamtdruck übernehmen"
            @click="copyFromReference('total_pressure')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Ventilator und Antrieb -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-fan</v-icon>
        <span>Ventilator und Antrieb</span>
      </div>

      <v-autocomplete
        v-model="form.fanType"
        :error-messages="validationErrors?.fan_type"
        label="Laufrad"
        :items="fanTypes"
        item-title="text"
        item-value="value"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.fan_type" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.fan_type }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.fan_type"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Laufrad übernehmen"
            @click="copyFromReference('fan_type')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <v-autocomplete
        v-model="form.regulation"
        :error-messages="validationErrors?.regulation"
        label="Regelung"
        :items="regulations"
        item-title="text"
        item-value="value"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.regulation" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.regulation }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.regulation"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Regelung übernehmen"
            @click="copyFromReference('regulation')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <v-autocomplete
        v-model="form.ratedPower"
        :error-messages="validationErrors?.rated_power"
        label="Leistungsklasse"
        :items="ratedPowers"
        item-title="power"
        item-value="id"
        suffix="kW"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.rated_power" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.rated_power }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.rated_power"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Leistungsklasse übernehmen"
            @click="copyFromReference('rated_power')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <v-autocomplete
        v-model="form.poles"
        :error-messages="validationErrors?.poles"
        label="Polzahl"
        :items="poleList"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.poles" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.poles }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.poles"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Polzahl übernehmen"
            @click="copyFromReference('poles')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <v-autocomplete
        v-model="form.efficiencyClass"
        :error-messages="validationErrors?.efficiency_class"
        label="Effizienzklasse"
        :items="efficiencyClasses"
        item-title="text"
        item-value="value"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.efficiency_class" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.efficiency_class }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.efficiency_class"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Effizienzklasse übernehmen"
            @click="copyFromReference('efficiency_class')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <v-autocomplete
        v-model="form.gear"
        :error-messages="validationErrors?.gear"
        label="Antrieb"
        :items="gears"
        item-title="text"
        item-value="value"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.gear" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.gear }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.gear"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Antrieb übernehmen"
            @click="copyFromReference('gear')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
    </div>

    <!-- Lastverteilung -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-gauge</v-icon>
        <span>Lastverteilung</span>
      </div>

      <v-text-field
        v-model.number="form.fullLoad"
        type="number"
        :error-messages="validationErrors?.full_load"
        label="Anteil Volllast"
        suffix="%"
        :hint="hint.full_load"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.full_load" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.full_load }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.full_load"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Volllastanteil übernehmen"
            @click="copyFromReference('full_load')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-text-field
        v-if="
          (form.fullLoad !== null && form.fullLoad < 100) ||
          form.partLoad75
        "
        v-model.number="form.partLoad75"
        type="number"
        :error-messages="validationErrors?.part_load_75"
        label="Anteil 75% Teillast"
        suffix="%"
        :hint="hint.part_load_75"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.part_load_75" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.part_load_75 }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.part_load_75"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für 75 Prozent Teillast übernehmen"
            @click="copyFromReference('part_load_75')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-text-field
        v-if="
          (form.fullLoad !== null && form.fullLoad < 100) ||
          form.partLoad50
        "
        v-model.number="form.partLoad50"
        type="number"
        :error-messages="validationErrors?.part_load_50"
        label="Anteil 50% Teillast"
        suffix="%"
        :hint="hint.part_load_50"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.part_load_50" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.part_load_50 }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.part_load_50"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für 50 Prozent Teillast übernehmen"
            @click="copyFromReference('part_load_50')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-text-field
        v-if="
          (form.fullLoad !== null && form.fullLoad < 100) ||
          form.partLoad25
        "
        v-model.number="form.partLoad25"
        type="number"
        :error-messages="validationErrors?.part_load_25"
        label="Anteil 25% Teillast"
        suffix="%"
        :hint="hint.part_load_25"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.part_load_25" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.part_load_25 }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.part_load_25"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für 25 Prozent Teillast übernehmen"
            @click="copyFromReference('part_load_25')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Betrieb -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-clock-outline</v-icon>
        <span>Betrieb</span>
      </div>

      <v-text-field
        v-model.number="form.uptime"
        type="number"
        :error-messages="validationErrors?.uptime"
        label="Jährliche Betriebsstunden"
        suffix="h/a"
        :hint="hint.uptime"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.uptime"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Betriebsstunden übernehmen"
            @click="copyFromReference('uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>
  </v-form>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useBaseForm } from '@/composables/useBaseForm'
import api from '@/plugins/axios'
import { djangoChoicesToVuetify } from '@/utils'

/** Props (match action.vue contract) */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference: { type: Boolean, default: false },
  planned: { type: Boolean, default: false },
  otherData: { type: Object, default: () => ({}) },
  sharedData: { type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

/** Pinia (for generator loading state) */

/** Field mapping (frontend -> backend) */
const fields = {
  actionId: 'action_id',
  amount: 'amount',
  volumeFlow: 'volume_flow',
  totalPressure: 'total_pressure',
  fanType: 'fan_type',
  regulation: 'regulation',
  ratedPower: 'rated_power',
  poles: 'poles',
  efficiencyClass: 'efficiency_class',
  gear: 'gear',
  fullLoad: 'full_load',
  partLoad75: 'part_load_75',
  partLoad50: 'part_load_50',
  partLoad25: 'part_load_25',
  uptime: 'uptime',
}

/** useBaseForm integration */
const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: { partLoad75: 0, partLoad50: 0, partLoad25: 0 },
  reference: computed(() => props.reference),
  planned: computed(() => props.planned),
  shared: computed(() => false), // not a shared form
  otherData: computed(() => props.otherData),
  sharedData: computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (event, payload) => emit(event, payload),
})

/** Local lists */
const fanTypes = ref([])
const regulations = ref([])
const ratedPowers = ref([])
const poleList = ref([2, 4, 6])
const efficiencyClasses = ref([])
const gears = ref([])

/** Fetchers (modern endpoints, keep cache adapter) */
async function fetchFanTypes() {
  try {
    const { data } = await api.get('/methods/fan2/fan-type/')
    fanTypes.value = djangoChoicesToVuetify(data) // -> [{ text, value }]
  } catch {}
}
async function fetchRegulations() {
  try {
    const { data } = await api.get('/methods/fan2/regulation/')
    regulations.value = djangoChoicesToVuetify(data)
  } catch {}
}
async function fetchRatedPowers() {
  try {
    const { data } = await api.get('/methods/fan2/rated_powers/')
    ratedPowers.value = data // [{ id, power }]
  } catch {}
}
async function fetchEfficiencyClasses() {
  try {
    const { data } = await api.get('/methods/fan2/efficiency-classes/')
    efficiencyClasses.value = djangoChoicesToVuetify(data)
  } catch {}
}
async function fetchGears() {
  try {
    const { data } = await api.get('/methods/fan2/gear/')
    gears.value = djangoChoicesToVuetify(data)
  } catch {}
}

/** Expose validate for parent save failure highlighting */
const formRef = ref(null)
function validateForm() {
  return formRef.value?.validate?.()
}

defineExpose({
  validateForm,
  fields: Object.values(fields),
})


/** Lifecycle */
onMounted(async () => {
  await Promise.all([
    fetchFanTypes(),
    fetchRegulations(),
    fetchRatedPowers(),
    fetchEfficiencyClasses(),
    fetchGears(),
  ])
  await fetchHints()
})
</script>

<style scoped lang="scss">
.fan-form {
  width: 100%;
}

/* Input fields */
.fan-form :deep(.v-field) {
  border-radius: 18px;
}

.fan-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Visual sections */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

.method-section:last-child {
  margin-bottom: 0;
}

/* Section headings */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);
  font-size: 1rem;
  font-weight: 700;
}

/* Nested part-load fields */
.fan-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Help and reference-copy actions */
.fan-form :deep(.v-input__append),
.fan-form :deep(.v-field__append-inner) {
  align-items: center;
}

.fan-form :deep(.v-input__append .v-icon),
.fan-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.fan-form :deep(.v-input__append .v-btn),
.fan-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.fan-form :deep(.v-input__append .v-btn:hover),
.fan-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Disabled states */
.fan-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

/* Responsive */
@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .fan-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>
